import { QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  type RouteObject,
} from "react-router";
import { Toaster } from "sonner";

import { createQueryClient } from "@/core/api/query-client";
import { createIdentity } from "@/core/auth/create-identity";
import type { IdentityAdapter } from "@/core/auth/identity";
import { SessionProvider } from "@/core/auth/session";
import { ThemeProvider, useTheme } from "@/core/theme/theme";
import { RequireSignIn } from "@/features/auth/RequireSignIn";
import { SignInPage } from "@/features/auth/SignInPage";
import { BrandScope } from "@/features/brands/BrandScope";
import { StartPage } from "@/features/brands/StartPage";
import { HomePage } from "@/features/home/HomePage";
import { InvitationsPage } from "@/features/invitations/InvitationsPage";
import { AppShell } from "@/layouts/AppShell";

import { PAGES } from "./pages";
import { RouteError, RouteLoading } from "./RouteError";
import { ROUTE_PATTERNS, ROUTES } from "./routes";

const routes: RouteObject[] = [
  { path: ROUTES.signIn, element: <SignInPage />, errorElement: <RouteError fullPage /> },
  {
    path: ROUTES.createAccount,
    element: <SignInPage mode="create" />,
    errorElement: <RouteError fullPage />,
  },
  {
    hydrateFallbackElement: <RouteLoading />,
    // Anything the screens below don't catch themselves: never React Router's developer page.
    errorElement: <RouteError fullPage />,
    element: (
      <RequireSignIn>
        <Outlet />
      </RequireSignIn>
    ),
    children: [
      { index: true, element: <StartPage /> },
      { path: ROUTES.invitations, element: <InvitationsPage /> },
      {
        // One brand's pages. The home ships with the app; every other screen downloads when first opened.
        path: ROUTE_PATTERNS.brand,
        element: (
          <BrandScope>
            <AppShell />
          </BrandScope>
        ),
        children: [
          { index: true, element: <HomePage /> },
          {
            path: ROUTE_PATTERNS.orders,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.dispatch()).OrdersPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.dispatch,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.dispatch()).DispatchPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.subscriptions,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.subscriptions()).SubscriptionsPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.subscription,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.subscription()).SubscriptionPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.customers,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.customers()).CustomersPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.customer,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.customer()).CustomerPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.reports,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.reports()).ReportsPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.reviews,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.reviews()).ReviewsPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.dishes,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.dishes()).DishesPage }),
          },
          {
            path: ROUTE_PATTERNS.plans,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.plans()).PlansPage }),
          },
          {
            path: ROUTE_PATTERNS.plan,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.plan()).PlanPage }),
          },
          {
            path: ROUTE_PATTERNS.menus,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.menus()).MenusPage }),
          },
          {
            path: ROUTE_PATTERNS.menuTemplates,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.templates()).TemplatesPage }),
          },
          {
            path: ROUTE_PATTERNS.menuTemplate,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.template()).TemplatePage }),
          },
          {
            path: ROUTE_PATTERNS.promoCodes,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.promoCodes()).PromoCodesPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.team,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.team()).TeamPage }),
          },
          // The old address (before the menu followed the old portal's names).
          { path: ROUTE_PATTERNS.oldSettings, element: <KeepSearch to={ROUTE_PATTERNS.brandSettings} /> },
          {
            path: ROUTE_PATTERNS.brandSettings,
            errorElement: <RouteError />,
            lazy: async () => ({
              Component: (await PAGES.settings()).SettingsPage,
            }),
          },
          {
            path: ROUTE_PATTERNS.money,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.money()).MoneyPage }),
          },
          {
            path: ROUTE_PATTERNS.payout,
            errorElement: <RouteError />,
            lazy: async () => ({ Component: (await PAGES.payout()).PayoutPage }),
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to={ROUTES.start} replace /> },
];

/** Redirects to a sibling path, keeping the query (e.g. ?tab=holidays). */
function KeepSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`../${to}${search}`} relative="path" replace />;
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} position="top-center" richColors closeButton />;
}

export function App({ identity }: { identity?: IdentityAdapter }) {
  const [queryClient] = useState(createQueryClient);
  const [identityAdapter] = useState(() => identity ?? createIdentity());
  // Made per app (not at import), so it starts from the address the app opens on (tests rely on this too).
  const [router] = useState(() => createBrowserRouter(routes));
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider identity={identityAdapter}>
          <RouterProvider router={router} />
          <ThemedToaster />
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
